import { useState, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { profileToQuality, resolveRenderProfile } from '../constants/renderProfiles.js';

/**
 * Quality presets for adaptive rendering
 * Balances visual quality vs performance across different hardware
 */
export const QUALITY_PRESETS = {
  low: {
    label: 'Basse (Mobile/iGPU)',
    geometrySegments: 32,        // PERF: Réduit de 64 à 32 (suffisant pour mobile/iGPU)
    shadowMapSize: 0,            // PERF: Shadows désactivées (économise un render pass complet)
    shadowMapType: THREE.BasicShadowMap,
    enableBloom: false,
    enableDOF: false,
    enableColorGrading: false,   // PERF: Désactivé sur mobile/iGPU
    enableSSAO: false,
    aaType: 'none',              // PERF: Pas d'AA sur mobile/iGPU
    causticsEnabled: false,
    causticsResolution: 0.010,
    envMapIntensity: 1.0,
    maxTextureSize: 256,         // PERF: Réduit pour limiter la bande passante VRAM sur iGPU
    maxInteractionFps: 30,       // PERF: Cap FPS pour éviter les frame drops sur iGPU
    pixelRatio: 'auto',          // Auto-detected based on device
  },
  medium: {
    label: 'Moyenne (Standard)',
    geometrySegments: 64,        // Segments modérés (bon rapport qualité/perf)
    shadowMapSize: 1024,         // Shadow maps standard
    shadowMapType: THREE.PCFSoftShadowMap,
    enableBloom: true,           // Bloom activé pour filament
    bloomResolution: 256,        // Résolution bloom basse
    bloomStrength: 1.0,
    bloomThreshold: 1.0,
    bloomRadius: 0.4,
    enableDOF: false,
    enableColorGrading: false,
    enableSSAO: false,
    aaType: 'FXAA',
    causticsEnabled: false,
    causticsResolution: 0.015,
    envMapIntensity: 1.5,
    maxTextureSize: 1024,
    maxInteractionFps: 45,
    pixelRatio: 'auto',          // Auto-detected based on device
  },
  high: {
    label: 'Haute (Desktop)',
    geometrySegments: 128,       // Qualité élevée pour géométrie lisse
    shadowMapSize: 2048,         // Shadow maps haute résolution
    shadowMapType: THREE.PCFSoftShadowMap, // Ombres douces
    enableBloom: true,           // Bloom HDR pour filament
    bloomResolution: 512,        // Résolution bloom modérée
    bloomStrength: 1.0,
    bloomThreshold: 1.0,
    bloomRadius: 0.4,
    enableDOF: false,            // DOF désactivé par défaut
    enableColorGrading: false,   // Color grading désactivé
    enableSSAO: false,
    aaType: 'FXAA',              // FXAA (bon rapport qualité/perf)
    causticsEnabled: false,      // Caustics désactivés par défaut
    causticsResolution: 0.018,
    envMapIntensity: 2.0,
    maxTextureSize: 2048,        // Textures haute résolution
    maxInteractionFps: 60,
    pixelRatio: 'auto',          // Auto-detected based on device
  },
  ultra: {
    label: 'Ultra (Workstation)',
    geometrySegments: 256,       // Qualité maximale pour géométrie lisse
    shadowMapSize: 4096,         // Shadow maps ultra haute résolution
    shadowMapType: THREE.VSMShadowMap, // Ombres douces professionnelles
    enableBloom: true,           // Bloom HDR pour filament et tissu rétroéclairé
    bloomResolution: 1024,       // Haute résolution pour glow détaillé
    bloomStrength: 1.0,          // Force du bloom calibrée
    bloomThreshold: 1.0,         // Seuil HDR (valeurs > 1.0 bloom)
    bloomRadius: 0.4,            // Rayon du glow
    enableDOF: true,             // Profondeur de champ cinématique
    enableColorGrading: true,    // Color grading cinématique
    enableSSAO: false,           // SSAO optionnel (coûteux)
    aaType: 'SMAA',              // Anti-aliasing haute qualité
    supersampling: 2.5,          // SSAA: Render at 2.5x resolution for best AA quality (Retina: 2.5x)
    causticsEnabled: true,       // Caustics photoréalistes au sol
    causticsResolution: 0.022,   // Résolution caustics optimisée
    envMapIntensity: 2.5,        // Réflexions environnement
    maxTextureSize: 4096,        // Textures haute résolution
    maxInteractionFps: 60,
    pixelRatio: 'auto',          // Auto-detected based on device
  },
};

/**
 * Detects GPU capabilities and recommends optimal quality preset
 * @returns {string} - Quality preset key ('low', 'medium', 'high', 'ultra')
 */
export function detectOptimalQuality() {
  try {
    // Create temporary renderer for GPU detection
    const canvas = document.createElement('canvas');
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
    const gl = renderer.getContext();

    // Get GPU info
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) {
      // Force context loss before dispose
      renderer.forceContextLoss();
      renderer.dispose();
      canvas.remove();
      return 'medium'; // Safe default if no debug info
    }

    const gpuVendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    const gpuRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

    console.log('[QualitySettings] GPU detected:', { gpuVendor, gpuRenderer });

    // Get max texture size (indicator of GPU power)
    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    const maxRenderbufferSize = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE);

    // Clean up - force context loss to free GPU resources
    renderer.forceContextLoss();
    renderer.dispose();
    canvas.remove();

    // Detection heuristics (prioritized by reliability)

    // 1. High-end Apple Silicon (M1/M2/M3/M4)
    if (/Apple/i.test(gpuVendor) && /M[1-4]/i.test(gpuRenderer)) {
      return 'ultra';
    }

    // 2. High-end NVIDIA (RTX/Quadro)
    if (/NVIDIA/i.test(gpuRenderer)) {
      if (/RTX\s*[3-4]\d{3}|RTX\s*[4-5]0\d{2}|Quadro\s*RTX/i.test(gpuRenderer)) {
        return 'ultra';
      }
      if (/RTX\s*[2-3]\d{3}|GTX\s*16\d{2}/i.test(gpuRenderer)) {
        return 'high';
      }
      // Match GTX 900 series (9xx) and GTX 1000-1599 series (10xx-15xx)
      if (/GTX\s*(9\d{2}|1[0-5]\d{2})/i.test(gpuRenderer)) {
        return 'medium';
      }
    }

    // 3. High-end AMD (Radeon RX 6000/7000 series)
    if (/AMD|ATI/i.test(gpuRenderer)) {
      if (/RX\s*[6-7]\d{3}/i.test(gpuRenderer)) {
        return 'ultra';
      }
      if (/RX\s*[5-6]\d{3}/i.test(gpuRenderer)) {
        return 'high';
      }
      if (/RX\s*[4-5]\d{2}/i.test(gpuRenderer)) {
        return 'medium';
      }
    }

    // 4. Integrated Intel (usually low-end)
    if (/Intel/i.test(gpuVendor)) {
      // Intel Iris Xe (decent integrated)
      if (/Iris\s*Xe/i.test(gpuRenderer)) {
        return 'medium';
      }
      // Intel UHD (basic integrated)
      if (/UHD/i.test(gpuRenderer)) {
        return 'low';
      }
      // Older Intel HD
      return 'low';
    }

    // 5. Fallback: Use max texture size as proxy
    if (maxTextureSize >= 16384 && maxRenderbufferSize >= 16384) {
      return 'ultra';
    }
    if (maxTextureSize >= 8192) {
      return 'high';
    }
    if (maxTextureSize >= 4096) {
      return 'medium';
    }

    // 6. Safe default for unknown GPUs
    return 'medium';
  } catch (error) {
    console.error('[QualitySettings] GPU detection failed:', error);
    return 'medium'; // Safe fallback
  }
}

/**
 * Detects device type (mobile/tablet/desktop) for additional context
 * @returns {string} - Device type ('mobile', 'tablet', 'desktop')
 */
export function detectDeviceType() {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  const isTablet = /ipad|android(?!.*mobile)|tablet/i.test(userAgent);

  if (isMobile) return 'mobile';
  if (isTablet) return 'tablet';
  return 'desktop';
}

/**
 * Intelligent pixel ratio detection
 * Balances visual quality with performance based on:
 * - Device pixel ratio (hardware capability)
 * - Screen size (larger screens benefit more from high DPI)
 * - GPU capability (estimated from quality preset)
 * - Total pixel count (performance impact)
 * - Battery status (if available)
 *
 * @param {string} qualityLevel - Quality preset ('low', 'medium', 'high', 'ultra')
 * @returns {number} - Optimal pixel ratio (1.0 to 3.0)
 */
export function detectOptimalPixelRatio(qualityLevel = 'medium') {
  const deviceDPR = window.devicePixelRatio || 1;
  const deviceType = detectDeviceType();

  // Screen dimensions
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const screenArea = screenWidth * screenHeight;

  // Canvas/viewport size (what we actually render to)
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const viewportArea = viewportWidth * viewportHeight;

  console.log('[PixelRatio] Detection inputs:', {
    deviceDPR,
    deviceType,
    screen: `${screenWidth}x${screenHeight}`,
    viewport: `${viewportWidth}x${viewportHeight}`,
    qualityLevel
  });

  // Base ratio from quality level
  const qualityBaseRatios = {
    low: 1.0,
    medium: 1.0,
    high: 1.5,
    ultra: 2.0
  };
  let targetRatio = qualityBaseRatios[qualityLevel] || 1.0;

  // === FACTOR 1: Device type constraints ===
  // Mobile devices have limited GPU/battery, cap their ratio
  if (deviceType === 'mobile') {
    targetRatio = Math.min(targetRatio, 1.5);
  } else if (deviceType === 'tablet') {
    targetRatio = Math.min(targetRatio, 2.0);
  }

  // === FACTOR 2: Screen density awareness ===
  // If device has low DPR, no point rendering higher
  // If device has very high DPR (3x), we might not need full resolution
  if (deviceDPR <= 1) {
    // Standard density screen - no benefit from high pixel ratio
    targetRatio = Math.min(targetRatio, 1.0);
  } else if (deviceDPR >= 3) {
    // Very high density (iPhone Pro, etc) - diminishing returns above 2x
    // Human eye can't distinguish beyond ~300 PPI at normal viewing distance
    targetRatio = Math.min(targetRatio, 2.5);
  }

  // === FACTOR 3: Screen size consideration ===
  // Large screens (4K monitors) need higher ratio to look sharp
  // Small screens (phones) don't benefit as much
  const isLargeScreen = screenArea >= 3840 * 2160; // 4K or larger
  const isMediumScreen = screenArea >= 1920 * 1080; // Full HD or larger
  const isSmallScreen = screenArea < 1280 * 720;

  if (isLargeScreen && qualityLevel === 'ultra') {
    // 4K monitors benefit from higher ratios
    targetRatio = Math.min(deviceDPR, 2.5);
  } else if (isSmallScreen) {
    // Small screens - save GPU power
    targetRatio = Math.min(targetRatio, 1.25);
  }

  // === FACTOR 4: Viewport vs Screen ratio ===
  // If canvas is small compared to screen, we can afford higher ratio
  // If canvas fills most of the screen, be more conservative
  const viewportToScreenRatio = viewportArea / screenArea;

  if (viewportToScreenRatio < 0.3) {
    // Small canvas (< 30% of screen) - can render at higher quality
    targetRatio = Math.min(targetRatio * 1.2, deviceDPR);
  } else if (viewportToScreenRatio > 0.8) {
    // Large canvas (> 80% of screen) - be conservative
    targetRatio = Math.min(targetRatio, deviceDPR * 0.8);
  }

  // === FACTOR 5: Supersampling for Ultra quality ===
  // Apply supersampling BEFORE pixel budget check so it gets properly constrained
  const supersamplingFactor = QUALITY_PRESETS[qualityLevel]?.supersampling || 1.0;
  if (supersamplingFactor > 1.0) {
    // SSAA: Use supersampling as the base, ensuring at least device DPR
    targetRatio = Math.max(supersamplingFactor, deviceDPR);
    console.log('[PixelRatio] SSAA target:', { supersamplingFactor, deviceDPR, targetRatio });
  }

  // === FACTOR 6: Total pixel budget check ===
  // Prevents rendering too many pixels on large screens or low-end hardware
  // This applies AFTER supersampling to ensure we don't exceed reasonable limits
  const totalPixels = viewportWidth * viewportHeight * targetRatio * targetRatio;

  // Adaptive max pixels based on quality level:
  // - Ultra: Up to 5K equivalent (~14.7M pixels) for workstations
  // - Others: 4K equivalent (~8.3M pixels)
  const maxReasonablePixels = qualityLevel === 'ultra' ? 14745600 : 8294400; // 5K vs 4K

  if (totalPixels > maxReasonablePixels) {
    // Scale back to stay within pixel budget
    const scaleFactor = Math.sqrt(maxReasonablePixels / totalPixels);
    const previousRatio = targetRatio;
    targetRatio = targetRatio * scaleFactor;
    console.log('[PixelRatio] Scaled back due to pixel budget:', {
      totalPixels,
      maxReasonablePixels,
      scaleFactor,
      previousRatio,
      newRatio: targetRatio
    });
  }

  // === FACTOR 7: Battery awareness (if available) ===
  // Reduce quality when on battery to save power
  if ('getBattery' in navigator) {
    try {
      const cachedBattery = window.__batteryStatus;
      if (cachedBattery && !cachedBattery.charging && cachedBattery.level < 0.3) {
        // Low battery and not charging - reduce ratio
        targetRatio = Math.min(targetRatio, 1.25);
        console.log('[PixelRatio] Reduced due to low battery');
      }
    } catch (e) {
      // Battery API not available or failed
    }
  }

  // === FACTOR 8: Memory pressure (Chrome-specific) ===
  if ('memory' in performance) {
    const memory = performance.memory;
    const usedRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
    if (usedRatio > 0.8) {
      // High memory pressure - reduce ratio
      targetRatio = Math.min(targetRatio, 1.0);
      console.log('[PixelRatio] Reduced due to memory pressure:', usedRatio);
    }
  }

  // === Final bounds ===
  if (supersamplingFactor > 1.0) {
    // Ultra with SSAA: Never go below device DPR (would make things blurry)
    // The pixel budget may have reduced us, but we still want at least native resolution
    targetRatio = Math.max(targetRatio, deviceDPR);
  } else {
    // Non-Ultra: Cap at device DPR (no benefit exceeding native resolution)
    targetRatio = Math.min(targetRatio, deviceDPR);
  }

  // Absolute minimum: 1.0
  targetRatio = Math.max(targetRatio, 1.0);

  // Round to nearest 0.25 for cleaner scaling
  targetRatio = Math.round(targetRatio * 4) / 4;

  console.log('[PixelRatio] Final decision:', {
    targetRatio,
    deviceDPR,
    reasoning: `Quality ${qualityLevel}, ${deviceType}, ${screenWidth}x${screenHeight}`
  });

  return targetRatio;
}

/**
 * Initialize battery status monitoring for pixel ratio optimization
 * Call this once at app startup
 */
export function initBatteryMonitoring() {
  if ('getBattery' in navigator) {
    navigator.getBattery().then(battery => {
      window.__batteryStatus = {
        charging: battery.charging,
        level: battery.level
      };

      battery.addEventListener('chargingchange', () => {
        window.__batteryStatus.charging = battery.charging;
      });

      battery.addEventListener('levelchange', () => {
        window.__batteryStatus.level = battery.level;
      });
    }).catch(() => {
      // Battery API failed
    });
  }
}

/**
 * Hook for managing quality settings with auto-detection and user override
 * @param {string} initialQuality - Optional initial quality override
 * @returns {object} - Quality settings and controls
 */
export function useQualitySettings(initialQuality = null, options = {}) {
  const { initialProfile = null } = options;

  // Auto-detect optimal quality on mount
  const detectedQuality = useMemo(() => {
    const gpuQuality = detectOptimalQuality();
    const deviceType = detectDeviceType();

    // Override for mobile devices (never go above medium)
    if (deviceType === 'mobile' && (gpuQuality === 'high' || gpuQuality === 'ultra')) {
      console.log('[QualitySettings] Mobile device detected, limiting to medium quality');
      return 'medium';
    }

    return gpuQuality;
  }, []);

  const resolvedProfile = useMemo(
    () => resolveRenderProfile(initialProfile),
    [initialProfile]
  );

  const resolvedInitialQuality = useMemo(() => {
    if (initialQuality && QUALITY_PRESETS[initialQuality]) {
      return initialQuality;
    }
    if (initialProfile) {
      const profileQuality = profileToQuality(resolvedProfile);
      if (!profileQuality) return detectedQuality; // 'auto' → use GPU detection
      // Cap profile quality by GPU detection (GPU acts as ceiling)
      const QUALITY_ORDER = ['low', 'medium', 'high', 'ultra'];
      const pIdx = QUALITY_ORDER.indexOf(profileQuality);
      const dIdx = QUALITY_ORDER.indexOf(detectedQuality);
      return QUALITY_ORDER[Math.min(pIdx, dIdx)];
    }
    return detectedQuality;
  }, [initialQuality, initialProfile, resolvedProfile, detectedQuality]);

  // Quality state (user can override)
  const [quality, setQuality] = useState(resolvedInitialQuality);

  // Keep quality reactive when parent-driven quality/profile changes.
  useEffect(() => {
    if (quality !== resolvedInitialQuality) {
      setQuality(resolvedInitialQuality);
    }
  }, [resolvedInitialQuality, quality]);

  // Get current preset with resolved pixel ratio
  const currentPreset = useMemo(() => {
    const basePreset = QUALITY_PRESETS[quality] || QUALITY_PRESETS.medium;

    // Resolve 'auto' pixel ratio to actual value
    let resolvedPixelRatio = basePreset.pixelRatio;
    if (resolvedPixelRatio === 'auto') {
      resolvedPixelRatio = detectOptimalPixelRatio(quality);
    }

    return {
      ...basePreset,
      pixelRatio: resolvedPixelRatio,
    };
  }, [quality]);

  // Log quality changes
  useEffect(() => {
    console.log('[QualitySettings] Quality preset changed:', quality, {
      ...currentPreset,
      pixelRatioResolved: currentPreset.pixelRatio
    });
  }, [quality, currentPreset]);

  return {
    quality,
    setQuality,
    preset: currentPreset,
    detectedQuality,
    deviceType: detectDeviceType(),
    availableQualities: Object.keys(QUALITY_PRESETS),
    qualityLabels: Object.fromEntries(
      Object.entries(QUALITY_PRESETS).map(([key, value]) => [key, value.label])
    ),
  };
}

/**
 * Get performance metrics for current quality preset
 * @param {string} quality - Quality preset key
 * @returns {object} - Estimated performance metrics
 */
export function getQualityMetrics(quality) {
  const preset = QUALITY_PRESETS[quality] || QUALITY_PRESETS.medium;

  // Estimated vertex count for lamp geometry
  const shadeVertices = preset.geometrySegments * 2 * 6; // Approximate
  const frameVertices = preset.geometrySegments * 8 * 10; // Approximate ribs
  const totalVertices = shadeVertices + frameVertices;

  // Estimated draw calls (rough estimate)
  const drawCalls = 5 + (preset.causticsEnabled ? 1 : 0);

  // Estimated memory usage (rough)
  const shadowMapMemory = (preset.shadowMapSize * preset.shadowMapSize * 4) / (1024 * 1024); // MB
  const textureMemory = ((preset.maxTextureSize * preset.maxTextureSize * 4) / (1024 * 1024)) * 3; // 3 textures avg
  const totalMemory = shadowMapMemory + textureMemory;

  return {
    vertices: totalVertices,
    drawCalls,
    estimatedMemoryMB: Math.round(totalMemory),
    targetFPS: quality === 'low' ? 30 : 60,
  };
}
